/**
 * Unified Comparison Engine for Candidate Ranking and Selection
 *
 * This module provides a single source of truth for ALL candidate comparison logic:
 * - Ordering candidates for display in the UI
 * - Selecting the best candidate for phase transitions
 * - Determining if a new candidate is better during best-fit search
 *
 * Uses three distinct strategies based on bond mix:
 * 1. Unlimited Strategy: Value-focused (principal maximization)
 * 2. Targeted Strategy: Efficiency-focused (best value for money)
 * 3. Mixed Strategy: Balanced approach (value with efficiency consideration)
 */

import {
  BondCategorizationEngine,
  type OrderingStrategyType,
} from '~/engine/processing/shared/bond-categorization';
import type { Candidate, Configuration } from '~/types';
import type { CandidateSortFunction } from './shared-utilities';
import { createCandidateRanking } from './shared-utilities';

export class ComparisonEngine {
  /**
   * Unlimited Strategy: Viability → Value → Utilization
   * For scenarios with only unlimited bonds
   */
  static sortByUnlimitedStrategy: CandidateSortFunction = (a, b) => {
    // 1. Viable candidates always ranked above non-viable
    if (a.isViable !== b.isViable) {
      return a.isViable ? -1 : 1;
    }

    // 2. Total principal (higher is better)
    // Direct comparison - any improvement counts
    const principalDiff = b.metrics.totalPrincipal - a.metrics.totalPrincipal;
    if (principalDiff !== 0) {
      return principalDiff > 0 ? 1 : -1;
    }

    // 3. Revenue utilization (higher is better) - tiebreaker
    const utilizationDiff = b.metrics.revenueUtilization - a.metrics.revenueUtilization;
    if (utilizationDiff !== 0) {
      return utilizationDiff > 0 ? 1 : -1;
    }

    // 4. Total interest (lower is better)
    const interestDiff = a.metrics.totalInterest - b.metrics.totalInterest;
    if (interestDiff !== 0) {
      return interestDiff < 0 ? -1 : 1;
    }

    // 5. If still tied, use candidate name as final tiebreaker
    return a.candidate.name.localeCompare(b.candidate.name);
  };

  /**
   * Targeted Strategy: Viability → Efficiency → Interest
   * For scenarios with only targeted bonds
   */
  static sortByTargetedStrategy: CandidateSortFunction = (a, b) => {
    // 1. Viable candidates always ranked above non-viable
    if (a.isViable !== b.isViable) {
      return a.isViable ? -1 : 1;
    }

    // 2. Efficiency (higher is better) - PRIMARY for targeted scenarios
    // Direct comparison - any improvement counts
    const efficiencyDiff = b.metrics.efficiency - a.metrics.efficiency;
    if (efficiencyDiff !== 0) {
      return efficiencyDiff > 0 ? 1 : -1;
    }

    // 3. When efficiency is exactly equal, prefer lower interest
    const interestDiff = a.metrics.totalInterest - b.metrics.totalInterest;
    if (interestDiff !== 0) {
      return interestDiff < 0 ? -1 : 1; // Lower interest is better
    }

    // 4. If still tied, check utilization as additional tiebreaker
    const utilizationDiff = b.metrics.revenueUtilization - a.metrics.revenueUtilization;
    if (utilizationDiff !== 0) {
      return utilizationDiff > 0 ? 1 : -1;
    }

    // 5. Final tiebreaker: candidate name
    return a.candidate.name.localeCompare(b.candidate.name);
  };

  /**
   * Mixed Strategy: Viability → Value → Efficiency → Interest
   * For scenarios with both targeted and unlimited bonds
   */
  static sortByMixedStrategy: CandidateSortFunction = (a, b) => {
    // 1. Viable candidates always ranked above non-viable
    if (a.isViable !== b.isViable) {
      return a.isViable ? -1 : 1;
    }

    // 2. Total principal (higher is better)
    // Direct comparison - any improvement counts
    const principalDiff = b.metrics.totalPrincipal - a.metrics.totalPrincipal;
    if (principalDiff !== 0) {
      return principalDiff > 0 ? 1 : -1;
    }

    // 3. Efficiency (higher is better) - secondary consideration
    const efficiencyDiff = b.metrics.efficiency - a.metrics.efficiency;
    if (efficiencyDiff !== 0) {
      return efficiencyDiff > 0 ? 1 : -1;
    }

    // 4. Total interest (lower is better) - important tiebreaker
    const interestDiff = a.metrics.totalInterest - b.metrics.totalInterest;
    if (interestDiff !== 0) {
      return interestDiff < 0 ? -1 : 1;
    }

    // 5. If still tied, use candidate name as final tiebreaker
    return a.candidate.name.localeCompare(b.candidate.name);
  };

  /**
   * Gets the appropriate sort function for a given ordering strategy
   */
  static getSortFunctionForStrategy(strategy: OrderingStrategyType): CandidateSortFunction {
    switch (strategy) {
      case 'unlimited':
        return ComparisonEngine.sortByUnlimitedStrategy;
      case 'targeted':
        return ComparisonEngine.sortByTargetedStrategy;
      case 'mixed':
        return ComparisonEngine.sortByMixedStrategy;
      default:
        return ComparisonEngine.sortByUnlimitedStrategy; // fallback
    }
  }

  /**
   * Orders candidates using the appropriate strategy based on bond mix
   * This function is used for BOTH display ordering AND phase transition candidate selection
   */
  static orderCandidates(candidates: Candidate[], configuration: Configuration): Candidate[] {
    if (candidates.length === 0) return [];

    const strategy = BondCategorizationEngine.determineOrderingStrategy(candidates, configuration);
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);

    // Create rankings for all candidates
    const rankings = candidates.map((candidate, index) => createCandidateRanking(candidate, index));

    // Sort using appropriate strategy
    const sortedRankings = rankings.sort(sortFunction).map((ranking, index) => ({
      ...ranking,
      rank: index + 1,
    }));

    return sortedRankings.map((ranking) => ranking.candidate);
  }

  /**
   * Gets the best candidate according to the appropriate strategy
   * This is used for phase transitions (first-fit → best-fit → optimization)
   */
  static getBestCandidateForNextPhase(
    candidates: Candidate[],
    configuration: Configuration
  ): Candidate | null {
    if (candidates.length === 0) return null;

    const orderedCandidates = ComparisonEngine.orderCandidates(candidates, configuration);
    return orderedCandidates[0]; // First candidate is the "best" according to strategy
  }

  /**
   * Determines if search should continue based on improvement.
   * Uses the SAME comparison logic as ordering for consistency.
   */
  static shouldContinueSearch(
    currentBest: Candidate,
    newCandidate: Candidate,
    strategy: OrderingStrategyType
  ): boolean {
    const currentRanking = createCandidateRanking(currentBest, 0);
    const newRanking = createCandidateRanking(newCandidate, 0);

    // Use the same comparison function as ordering
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);

    // If new candidate ranks higher (negative result means new is better), continue search
    const comparisonResult = sortFunction(newRanking, currentRanking);

    return comparisonResult < 0;
  }

  /**
   * Enhanced logging with strategy information
   */
  static logCandidateRankings(
    candidates: Candidate[],
    configuration: Configuration,
    title: string = 'Candidate Rankings'
  ): void {
    if (candidates.length === 0) return;

    const strategy = BondCategorizationEngine.determineOrderingStrategy(candidates, configuration);
    const strategyDescription = BondCategorizationEngine.getOrderingStrategyDescription(strategy);
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);

    const rankings = candidates.map((candidate, index) => createCandidateRanking(candidate, index));
    const sortedRankings = rankings.sort(sortFunction).map((ranking, index) => ({
      ...ranking,
      rank: index + 1,
    }));

    console.log(`\n📊 ${title} (${strategyDescription})`);
    console.log('=====================================');

    sortedRankings.forEach((ranking) => {
      const status = ranking.isViable ? '✅' : '❌';
      const metrics = ranking.metrics;

      console.log(`${ranking.rank}. ${status} ${ranking.candidate.name}`);
      console.log(`   Principal: $${metrics.totalPrincipal.toLocaleString()}`);
      console.log(`   Efficiency: ${(metrics.efficiency * 100).toFixed(4)}%`);
      console.log(`   Utilization: ${(metrics.revenueUtilization * 100).toFixed(4)}%`);
      console.log(`   Interest: $${metrics.totalInterest.toLocaleString()}`);
    });

    console.log('=====================================\n');
  }

  /**
   * Gets the primary metric for a strategy.
   */
  static getPrimaryMetric(candidate: Candidate, strategy: OrderingStrategyType): number {
    switch (strategy) {
      case 'unlimited':
        return candidate.results.totalPrincipal;
      case 'targeted':
        return candidate.results.efficiency || 0;
      case 'mixed':
        return candidate.results.totalPrincipal; // Primary is still value in mixed mode
      default:
        return 0;
    }
  }

  /**
   * Compatibility layer for legacy code - maps old strategy names to new
   * @deprecated Use OrderingStrategyType directly
   */
  static getLegacySortFunction(useTargetAware: boolean): CandidateSortFunction {
    // Map legacy binary choice to new strategy system
    // If target-aware was requested, we'll use targeted strategy
    // Otherwise use unlimited strategy
    return useTargetAware
      ? ComparisonEngine.sortByTargetedStrategy
      : ComparisonEngine.sortByUnlimitedStrategy;
  }
}
